<template>
  <div class="selection-area row d-flex">
    <div
      v-for="(pkg, index) in getPackages()"
      :key="index"
      :class="{ disabled: !isAllowed(pkg) }"
      class="col-md-6 col-lg-4 mb-2"
    >
      <div
        @click="select(pkg)"
        class="selection-item h-100"
        :class="{ selected: pkg.id === value, disabled: !isAllowed(pkg) }"
      >
        <div class="package h-100 d-flex flex-column">
          <div class="package-header">
            <div class="d-flex align-items-baseline">
              <div class="price">{{ getPrice(pkg) }}</div>
              <div>{{ getFrequency(pkg) }}</div>
            </div>
            <div class="name">{{ getName(pkg) }}</div>
          </div>
          <div class="attributes-section flex-fill">
            <ul>
              <li>{{ getSites(pkg) }}</li>
              <li v-if="isWP(pkg)">{{ getStagingEnvironments(pkg) }}</li>
              <li v-if="isWP(pkg)">{{ getBackups() }}</li>
              <li v-if="isWoo(pkg)">{{ getRecommendedUsage(pkg) }}</li>
              <li v-if="isWoo(pkg) && getAddons(pkg)">{{ getAddons(pkg) }}</li>
              <li v-if="pkg.diskSpace">{{ getDiskSpace(pkg) }}</li>
              <li v-if="pkg.bandwidth">{{ getBandwidth(pkg) }}</li>
              <li v-if="pkg.ram">{{ getMemory(pkg) }}</li>
              <li v-if="pkg.cpus">{{ getCpus(pkg) }}</li>
            </ul>
          </div>
          <div class="d-flex justify-content-center">
            <Btn :variant="pkg.id === value ? 'primary' : 'primaryOutline'">
              {{ $t("global.select") }}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Btn from "@/components/display/Btn";
import { fromGBFormat } from "leatherman-js/utils/formatters/size";
import { moneyFormat } from "leatherman-js/utils/formatters/money";

export default {
  name: "FormPackageSelect",
  props: {
    packages: {
      type: Array,
      required: true,
    },
    planSiteCount: Number,
    planTerm: {
      type: Number,
      required: true,
    },
    appId: Number,
    value: Number,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Btn,
  },
  setup(props, { emit, root }) {
    const getAddons = (pkg) => root.$t(`global.package-woo-${pkg.name.toLowerCase()}-addons`);
    const getBackups = () => root.$t("global.package-30-day-backups");
    const getBandwidth = (pkg) =>
      root.$t("global.package-bandwidth", { GB: fromGBFormat(pkg.bandwidth) });
    const getDiskSpace = (pkg) =>
      root.$t("global.package-disk-space", { GB: fromGBFormat(pkg.diskSpace) });
    const getMemory = (pkg) => root.$t("global.package-memory", { GB: fromGBFormat(pkg.ram) });
    const getCpus = (pkg) => root.$t("global.package-cpus", { cpus: pkg.cpus });
    const getFrequency = () => `/ ${props.planTerm === 12 ? "yr" : "mo"}`;
    const getName = (pkg) => pkg.name;
    const getPackages = () => props.packages.sort((a, b) => a.monthlyFee - b.monthlyFee);
    const getPrice = (pkg) => {
      if (props.appId) {
        return moneyFormat(pkg.pricing[props.planTerm] + pkg.appPricing[props.appId]);
      }
      return moneyFormat(pkg.pricing[props.planTerm]);
    };
    const getRecommendedUsage = (pkg) => {
      const usage = {
        Starter: 500,
        Creator: 1000,
        Merchant: 2500,
        Standard: 3000,
        Growth: 5000,
        Enterprise: 10000,
      };
      return root.$t("global.package-recommended-usage", { usage: usage[pkg.name] });
    };
    const getSites = (pkg) => {
      return pkg.allottedSites > 1
        ? root.$t(isWoo(pkg) ? "global.package-woo-sites" : "global.package-sites", {
            sites: pkg.allottedSites,
          })
        : root.$t(isWoo(pkg) ? "global.package-woo-site" : "global.package-site");
    };
    const getStagingEnvironments = (pkg) =>
      root.$t(
        pkg.allottedSites > 1
          ? "global.package-staging-environments"
          : "global.package-staging-environment"
      );

    const isAllowed = (pkg) =>
      typeof props.planSiteCount !== "undefined"
        ? !props.disabled && props.planSiteCount <= pkg.allottedSites
        : true;
    const isWoo = (pkg) => pkg.label.startsWith("mwc.");
    const isWP = (pkg) => pkg.label.startsWith("mwp.");

    const select = (pkg) => emit("input", pkg.id);

    return {
      getAddons,
      getBackups,
      getBandwidth,
      getDiskSpace,
      getFrequency,
      getMemory,
      getCpus,
      getName,
      getPackages,
      getPrice,
      getRecommendedUsage,
      getSites,
      getStagingEnvironments,
      isAllowed,
      isWoo,
      isWP,
      select,
    };
  },
};
</script>

<style lang="scss"></style>
